import { useMemo, useState } from 'react';
import type { CoreDepartment, CoreEmployee, CoreOrganization, UserLabel } from '@/lib/zifekCore';

interface Props {
  organizations: CoreOrganization[];
  departments: CoreDepartment[];
  employees: CoreEmployee[];
  users: Record<number, UserLabel>;
}

export default function OrgChartPanel({ organizations, departments, employees, users }: Props) {
  const [orgId, setOrgId] = useState<number | null>(organizations[0]?.id ?? null);

  const org = useMemo(
    () => organizations.find((o) => o.id === orgId) || organizations[0] || null,
    [organizations, orgId],
  );

  const orgDepartments = useMemo(
    () => (org ? departments.filter((d) => d.organization_id === org.id) : []),
    [departments, org],
  );
  const orgEmployees = useMemo(
    () => (org ? employees.filter((e) => e.organization_id === org.id) : []),
    [employees, org],
  );

  const roots = useMemo(() => {
    const ids = new Set(orgDepartments.map((d) => d.id));
    return orgDepartments.filter((d) => !d.parent_department_id || !ids.has(d.parent_department_id));
  }, [orgDepartments]);

  const childrenOf = (parentId: number) => orgDepartments.filter((d) => d.parent_department_id === parentId);
  const employeesOf = (deptId: number | null) => orgEmployees.filter((e) => e.department_id === deptId);

  const userLabel = (id: number | null) => {
    if (!id) return null;
    const u = users[id];
    return u?.name || u?.user_name || `#${id}`;
  };

  if (organizations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
        <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
          <i className="ri-organization-chart text-2xl text-foreground-400"></i>
        </div>
        <p className="text-foreground-600">Aucune organisation à afficher.</p>
      </div>
    );
  }

  const DeptCard = ({ dept, depth }: { dept: CoreDepartment; depth: number }) => {
    const manager = userLabel(dept.manager_user_id);
    const emps = employeesOf(dept.id);
    const kids = childrenOf(dept.id);
    const inactive = dept.status === 'inactive';
    return (
      <div className={depth > 0 ? 'pl-5 ml-3 border-l-2 border-background-300/60' : ''}>
        <div className={`relative bg-background-50 border rounded-lg p-4 ${inactive ? 'opacity-60 border-background-200/70' : 'border-background-200/70'}`}>
          {depth > 0 && <span className="absolute -left-5 top-6 w-5 h-px bg-background-300/60"></span>}
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-lg bg-accent-100 text-accent-900 flex items-center justify-center flex-shrink-0">
                <i className="ri-node-tree"></i>
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-foreground-950 truncate">{dept.name}</div>
                <div className="text-[11px] text-foreground-400 flex items-center gap-1.5 truncate">
                  <i className="ri-user-star-line"></i>
                  {manager || 'Responsable non défini'}
                </div>
              </div>
            </div>
            <span className="text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap bg-background-100 text-foreground-600">
              {emps.length} employé{emps.length > 1 ? 's' : ''}
            </span>
          </div>
          {emps.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {emps.slice(0, 6).map((e) => {
                const label = e.user_id ? userLabel(e.user_id) : e.email;
                return (
                  <span key={e.id} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 font-medium whitespace-nowrap">
                    <i className="ri-user-3-line"></i>
                    {label || `#${e.id}`}
                  </span>
                );
              })}
              {emps.length > 6 && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 font-medium">+{emps.length - 6}</span>
              )}
            </div>
          )}
        </div>
        {kids.length > 0 && (
          <div className="mt-3 space-y-3">
            {kids.map((k) => <DeptCard key={k.id} dept={k} depth={depth + 1} />)}
          </div>
        )}
      </div>
    );
  };

  const noDept = employeesOf(null);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <label className="text-xs font-medium text-foreground-600">Organisation</label>
        <select
          value={org?.id ?? ''}
          onChange={(e) => setOrgId(Number(e.target.value))}
          className="px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-300"
        >
          {organizations.map((o) => (
            <option key={o.id} value={o.id}>{o.commercial_name || o.legal_name || `Organisation #${o.id}`}</option>
          ))}
        </select>
      </div>

      {!org ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
          <p className="text-foreground-600">Sélectionnez une organisation.</p>
        </div>
      ) : (
        <div className="bg-background-100/50 border border-background-200/70 rounded-xl p-5">
          {/* Racine : l'organisation */}
          <div className="bg-primary-500 text-background-50 rounded-lg p-4 flex items-center justify-between gap-3 max-w-md mx-auto">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-lg bg-background-50/20 flex items-center justify-center flex-shrink-0">
                <i className="ri-building-2-line text-xl"></i>
              </div>
              <div className="min-w-0">
                <div className="text-sm font-bold truncate">{org.commercial_name || org.legal_name || `Org #${org.id}`}</div>
                <div className="text-[11px] text-background-50/80 truncate">
                  Propriétaire : {userLabel(org.owner_user_id) || '—'}
                </div>
              </div>
            </div>
            <span className="text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap bg-background-50/20">
              {orgEmployees.length} employé{orgEmployees.length > 1 ? 's' : ''}
            </span>
          </div>

          {/* Arbre des départements */}
          <div className="mt-5 space-y-3">
            {roots.length === 0 ? (
              <p className="text-center text-sm text-foreground-500 py-6">
                Aucun département défini pour cette organisation.
              </p>
            ) : (
              roots.map((d) => <DeptCard key={d.id} dept={d} depth={0} />)
            )}
          </div>

          {/* Employés sans département */}
          {noDept.length > 0 && (
            <div className="mt-5 pt-4 border-t border-background-200/70">
              <div className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">
                Sans département ({noDept.length})
              </div>
              <div className="flex flex-wrap gap-1.5">
                {noDept.map((e) => {
                  const label = e.user_id ? userLabel(e.user_id) : e.email;
                  return (
                    <span key={e.id} className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full bg-background-50 border border-background-200/70 text-foreground-700 font-medium whitespace-nowrap">
                      <i className="ri-user-line"></i>
                      {label || `#${e.id}`}
                      {e.job_title ? <span className="text-foreground-400">· {e.job_title}</span> : null}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}